import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { rolesApi, PermissionDto } from '../api/rolesApi';

const PermissionsManagementPage: React.FC = () => {
  const [permissions, setPermissions] = useState<PermissionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<PermissionDto | null>(null);
  const [deletingPermission, setDeletingPermission] = useState<PermissionDto | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModule, setFilterModule] = useState('');

  // Form fields
  const [permissionName, setPermissionName] = useState('');
  const [description, setDescription] = useState('');
  const [module, setModule] = useState('');

  const loadPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await rolesApi.getAllPermissions();
      setPermissions(data);
    } catch {
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPermissions();
  }, [loadPermissions]);

  // Get unique modules for filter dropdown
  const modules = Array.from(new Set(permissions.map((p) => p.module))).sort();

  // Filtered permissions
  const filtered = permissions.filter((p) => {
    const matchSearch =
      !searchTerm ||
      p.permissionName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchModule = !filterModule || p.module === filterModule;
    return matchSearch && matchModule;
  });

  const resetForm = () => {
    setPermissionName('');
    setDescription('');
    setModule('');
    setEditingPermission(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (permission: PermissionDto) => {
    setEditingPermission(permission);
    setPermissionName(permission.permissionName);
    setDescription(permission.description);
    setModule(permission.module);
    setDialogOpen(true);
  };

  const openDeleteDialog = (permission: PermissionDto) => {
    setDeletingPermission(permission);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!permissionName.trim() || !module.trim()) {
      toast.error('Permission name and module are required');
      return;
    }

    setSaving(true);
    try {
      if (editingPermission) {
        await rolesApi.updatePermission(editingPermission.id, {
          id: editingPermission.id,
          permissionName: permissionName.trim(),
          description: description.trim(),
          module: module.trim(),
        });
        toast.success('Permission updated');
      } else {
        await rolesApi.createPermission({
          permissionName: permissionName.trim(),
          description: description.trim(),
          module: module.trim(),
        });
        toast.success('Permission created');
      }
      setDialogOpen(false);
      resetForm();
      await loadPermissions();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to save permission';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPermission) return;
    setSaving(true);
    try {
      await rolesApi.deletePermission(deletingPermission.id);
      toast.success('Permission deleted');
      setDeleteDialogOpen(false);
      setDeletingPermission(null);
      await loadPermissions();
    } catch {
      toast.error('Failed to delete permission');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Permissions Management
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog} sx={{ textTransform: 'none' }}>
          Add Permission
        </Button>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          size="small"
          placeholder="Search permissions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 250 }}
        />
        <TextField
          select
          size="small"
          label="Module"
          value={filterModule}
          onChange={(e) => setFilterModule(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All Modules</MenuItem>
          {modules.map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Permission Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, width: 100 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No permissions found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {p.permissionName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {p.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={p.module} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="primary" onClick={() => openEditDialog(p)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => openDeleteDialog(p)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="body2" color="text.secondary" mt={2}>
        Total: {filtered.length} permission{filtered.length !== 1 ? 's' : ''}
        {filterModule && ` in ${filterModule}`}
      </Typography>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingPermission ? 'Edit Permission' : 'Add Permission'}
          <IconButton size="small" onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Permission Name"
              required
              fullWidth
              value={permissionName}
              onChange={(e) => setPermissionName(e.target.value)}
              multiline
              minRows={3}
              inputProps={{ maxLength: 50 }}
              placeholder="e.g. ViewReports"
              helperText={`${permissionName.length}/50 characters`}
            />
            <TextField
  label="Description"
  value={description}
  onChange={(e) => {
    const value = e.target.value;
    if (value.length <= 200) setDescription(value);
  }}
  fullWidth
  multiline
  minRows={3}
  inputProps={{ maxLength: 200 }}
  helperText={`${description.length}/200 characters`}
/>
            <TextField
              label="Module"
              required
              fullWidth
              value={module}
              onChange={(e) => setModule(e.target.value)}
              placeholder="e.g. Reports"
              inputProps={{maxLength : 50}}
              helperText={`${module.length}/50 characters`}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none' }}>
            {saving ? <CircularProgress size={20} /> : editingPermission ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Permission</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deletingPermission?.permissionName}</strong>?
            This will also remove it from all roles.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving} sx={{ textTransform: 'none' }}>
            {saving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionsManagementPage;
